import React, { useState } from 'react';
import {
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CForm,
  CFormGroup,
  CLabel,
  CInput,
  CModalTitle,
  CButton,
} from '@coreui/react';
import _t from 'counterpart';
import { findErrorFromValidation, handleFormError } from '../../../helpers';
import ButtonWithLoader from '../../../components/ButtonWithLoader';
import Textarea from 'react-expanding-textarea';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { ICustomer } from '../types';

interface IProps {
  show: boolean;
  onClose: () => void;
  onCloseDiscardChanges: () => void;
  customer: ICustomer;
  titleKey: string; // e.g., 'customer.send-message-to', 'customer.send-sms-to', 'customer.send-whatsapp-to'
  successToastKey: string; // e.g., 'customer.message-sent-to-customer', 'customer.sms-sent-to-customer'
  inputId: string; // e.g., 'message', 'sms-message', 'whatsapp-message'
  onSend: (params: { customerId: string; message: string; subject?: string }) => Promise<any>; // Function to send the message
  showSubject?: boolean; // Whether to show subject field (default: false)
  useNumericId?: boolean; // Whether to use customer.numericId instead of customer.id (default: false)
  maxLength?: number; // Optional character cap for the message; shows a counter when set
}

const UnifiedMessageCustomerModal = ({
  show,
  onClose,
  onCloseDiscardChanges,
  customer,
  titleKey,
  successToastKey,
  inputId,
  onSend,
  showSubject = false,
  useNumericId = false,
  maxLength,
}: IProps) => {
  const [subject, setSubject] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [discardChangesModalShow, setDiscardChangesModalShow] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const sendMessageMutation = useMutation(
    (params: { customerId: string; message: string; subject?: string }) => onSend(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('customer-communications');
        toast.success(_t(successToastKey));
        setDiscardChangesModalShow(false);
        setSubject('');
        setMessage('');
        onClose();
      },
      onError: (error: any) => {
        handleFormError(error);
      },
    }
  );

  const close = () => {
    setDiscardChangesModalShow(false);
    setSubject('');
    setMessage('');
    sendMessageMutation.reset();
  };

  const handleClose = () => {
    const hasChanges = showSubject ? subject !== '' || message !== '' : message !== '';
    if (hasChanges) {
      setDiscardChangesModalShow(true);
    } else {
      sendMessageMutation.reset();
    }
    onClose();
  };

  const handleCloseDiscardChanges = () => {
    setDiscardChangesModalShow(false);
    onCloseDiscardChanges();
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubject(e.currentTarget.value);
  };

  const handleMessageChange = (e: React.FormEvent<any>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    setMessage(target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const customerId = useNumericId ? customer.numericId.toString() : customer.id;
    const params: { customerId: string; message: string; subject?: string } = {
      customerId,
      message,
    };
    if (showSubject) {
      params.subject = subject;
    }
    sendMessageMutation.mutate(params);
  };

  const findError = (paramName: string) => {
    return findErrorFromValidation(sendMessageMutation.error, paramName);
  };

  return (
    <>
      <CModal show={show} onClose={handleClose} closeOnBackdrop={false}>
        <CModalHeader closeButton>
          <CModalTitle>{_t(titleKey, { customer: customer.name })}</CModalTitle>
        </CModalHeader>
        <CForm onSubmit={handleSubmit}>
          <CModalBody>
            {showSubject && (
              <CFormGroup>
                <CLabel htmlFor="subject">{_t('emails.subject')}</CLabel>
                <CInput type="text" id="subject" name="subject" value={subject} onChange={handleSubjectChange} />
                {findError('subject') && <CLabel className="text-danger">{findError('subject')}</CLabel>}
              </CFormGroup>
            )}
            <CFormGroup>
              <CLabel htmlFor={inputId}>{_t('emails.text')}</CLabel>
              <Textarea
                id={inputId}
                className="w-100 mt-2 comments-input"
                onChange={handleMessageChange}
                value={message}
                rows={3}
                maxLength={maxLength}
              />
              {maxLength !== undefined && (
                <div className="text-muted small text-right">
                  {message.length}/{maxLength}
                </div>
              )}
            </CFormGroup>
            {findError('message') && <CLabel className="text-danger">{findError('message')}</CLabel>}
            {findError('text') && <CLabel className="text-danger">{findError('text')}</CLabel>}
          </CModalBody>
          <CModalFooter>
            <ButtonWithLoader
              isLoading={sendMessageMutation.isLoading}
              type="submit"
              buttonColor="primary"
              spinnerColor="secondary"
              title={_t.translate('action.send')}
              className="mr-2"
            />
            <CButton color="light" variant="outline" onClick={handleClose} disabled={sendMessageMutation.isLoading}>
              {_t('global.cancel')}
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>
      <CModal show={discardChangesModalShow} size="sm" closeOnBackdrop={false}>
        <CModalHeader className="d-flex flex-column">
          <CModalTitle>{_t('customer.discard-changes')}</CModalTitle>
        </CModalHeader>
        <CModalBody />
        <CModalFooter className="modal-footer--alternative">
          <CButton color="primary" onClick={close}>
            {_t('global.confirm')}
          </CButton>
          <CButton color="light" variant="outline" onClick={handleCloseDiscardChanges}>
            {_t('global.cancel')}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default React.memo(UnifiedMessageCustomerModal);
